import {
  PropertyFilterQuery,
  PropertyFilterToken,
  PropertyFilterTokenGroup,
} from "@amzn/awsui-collection-hooks";
import { ExtendedFilteringProperty } from "./definitions";

/**
 * Backend API format for PropertyFilter queries
 */
export interface BackendPropertyFilterQuery {
  operation: "and" | "or";
  tokens?: BackendPropertyFilterToken[];
  groups?: BackendPropertyFilterQuery[];
}

export interface BackendPropertyFilterToken {
  propertyKey?: string; // undefined for free text search
  operator:
    | "="
    | "!="
    | ":"
    | "!:"
    | "^"
    | "!^"
    | ">"
    | ">="
    | "<"
    | "<="
    | "in"
    | "not_in"
    | "exists"
    | "not_exists";
  value?: string;
  values?: string[];
}

/**
 * Sentinel value used for "no date" in date fields
 * Tasks without a date are stored as "1970-01-01T00:00:00Z" in DDB/OpenSearch
 */
export const DATE_SENTINEL_VALUE = "1970-01-01";

/**
 * Converts AWS UI PropertyFilter query to backend format
 * Handles both simple tokens and tokenGroups (when enableTokenGroups=true)
 * Automatically converts multiselect enum fields (array values) to IN/NOT_IN operators
 * Automatically excludes "1970-01-01" (sentinel value) for any date fields when filtering
 *
 * @param uiQuery - The PropertyFilter query from AWS UI components
 * @param filteringProperties - Array of filtering property definitions with fieldType metadata
 * @returns Backend-formatted query ready for OpenSearch
 *
 * Example usage and transformation scenarios:
 *
 * SCENARIO 1: Simple tokens (enableTokenGroups=false)
 * UI Query:
 * {
 *   operation: "and",
 *   tokens: [
 *     { propertyKey: "status", operator: "=", value: "In progress" },
 *     { propertyKey: "owners", operator: ":", value: "john" }
 *   ]
 * }
 *
 * Backend Result:
 * {
 *   operation: "and",
 *   tokens: [
 *     { propertyKey: "status", operator: "=", value: "In progress" },
 *     { propertyKey: "owners", operator: ":", value: "john" }
 *   ]
 * }
 *
 * SCENARIO 2: Multiselect enum fields (array values)
 * UI Query:
 * {
 *   operation: "and",
 *   tokens: [
 *     { propertyKey: "owners", operator: "=", value: ["alice", "bob"] },
 *     { propertyKey: "status", operator: "!=", value: ["closed", "archived"] }
 *   ]
 * }
 *
 * Backend Result:
 * {
 *   operation: "and",
 *   tokens: [
 *     { propertyKey: "owners", operator: "in", values: ["alice", "bob"] },
 *     { propertyKey: "status", operator: "not_in", values: ["closed", "archived"] }
 *   ]
 * }
 *
 * SCENARIO 3: Token groups (enableTokenGroups=true)
 * UI Query:
 * {
 *   operation: "and",
 *   tokens: [], // ignored when tokenGroups is present
 *   tokenGroups: [
 *     { propertyKey: "status", operator: "=", value: "In progress" }, // individual token
 *     {
 *       operation: "or", // token group
 *       tokens: [
 *         { propertyKey: "priority", operator: "=", value: "High" },
 *         { propertyKey: "priority", operator: "=", value: "Medium" }
 *       ]
 *     }
 *   ]
 * }
 *
 * Backend Result:
 * {
 *   operation: "and",
 *   tokens: [
 *     { propertyKey: "status", operator: "=", value: "In progress" }
 *   ],
 *   groups: [
 *     {
 *       operation: "or",
 *       tokens: [
 *         { propertyKey: "priority", operator: "=", value: "High" },
 *         { propertyKey: "priority", operator: "=", value: "Medium" }
 *       ]
 *     }
 *   ]
 * }
 *
 * Logical Expression: status = "In progress" AND (priority = "High" OR priority = "Medium")
 *
 * SCENARIO 4: Complex nested groups with multiselect enums and date filters
 * UI Query:
 * {
 *   operation: "and",
 *   tokenGroups: [
 *     { propertyKey: "status", operator: "=", value: "In progress" },
 *     {
 *       operation: "or",
 *       tokens: [
 *         { propertyKey: "owners", operator: "=", value: ["alice (Guest)", "bob"] },
 *         {
 *           operation: "and",
 *           tokens: [
 *             { propertyKey: "priority", operator: "=", value: "High" },
 *             { propertyKey: "dueDate", operator: "<=", value: "2025-12-31" }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * Backend Result:
 * {
 *   operation: "and",
 *   tokens: [
 *     { propertyKey: "status", operator: "=", value: "In progress" }
 *   ],
 *   groups: [
 *     {
 *       operation: "or",
 *       tokens: [
 *         { propertyKey: "owners", operator: "in", values: ["alice", "bob"] }
 *       ],
 *       groups: [
 *         {
 *           operation: "and",
 *           tokens: [
 *             { propertyKey: "priority", operator: "=", value: "High" },
 *             { propertyKey: "dueDate", operator: "<=", value: "2025-12-31" },
 *             { propertyKey: "dueDate", operator: ">", value: "1970-01-01" }  // Auto-added
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * Logical Expression:
 * status = "In progress" AND (
 *   owners IN ["alice", "bob"] OR
 *   (priority = "High" AND dueDate <= "2025-12-31" AND dueDate > "1970-01-01")
 * )
 *
 * SCENARIO 5: Smart exclusion - no redundant filter when user already has > filter
 * UI Query:
 * {
 *   operation: "and",
 *   tokens: [
 *     { propertyKey: "dueDate", operator: ">", value: "2026-01-01" },
 *     { propertyKey: "dueDate", operator: "<", value: "2026-02-01" }
 *   ]
 * }
 *
 * Backend Result:
 * {
 *   operation: "and",
 *   tokens: [
 *     { propertyKey: "dueDate", operator: ">", value: "2026-01-01" },
 *     { propertyKey: "dueDate", operator: "<", value: "2026-02-01" }
 *     // No sentinel exclusion added because > "2026-01-01" already excludes "1970-01-01"
 *   ]
 * }
 *
 * Logical Expression: dueDate > "2026-01-01" AND dueDate < "2026-02-01"
 *
 * SCENARIO 6: OR operation with date filters - surgical wrapping
 * UI Query:
 * {
 *   operation: "or",
 *   tokens: [
 *     { propertyKey: "owners", operator: "=", value: ["user1", "user2"] },
 *     { propertyKey: "dueDate", operator: "<=", value: "2026-01-01" }
 *   ]
 * }
 *
 * Backend Result:
 * {
 *   operation: "or",
 *   tokens: [
 *     { propertyKey: "owners", operator: "in", values: ["user1", "user2"] }
 *   ],
 *   groups: [
 *     {
 *       operation: "and",
 *       tokens: [
 *         { propertyKey: "dueDate", operator: "<=", value: "2026-01-01" },
 *         { propertyKey: "dueDate", operator: ">", value: "1970-01-01" }
 *       ]
 *     }
 *   ]
 * }
 *
 * Logical Expression: owners IN ["user1", "user2"] OR (dueDate <= "2026-01-01" AND dueDate > "1970-01-01")
 */
export function transformPropertyFilterQuery(
  uiQuery: PropertyFilterQuery,
  filteringProperties: ExtendedFilteringProperty[],
): BackendPropertyFilterQuery {
  const result: BackendPropertyFilterQuery = {
    operation: uiQuery.operation,
  };

  // Handle tokenGroups (when enableTokenGroups=true)
  if (uiQuery.tokenGroups && uiQuery.tokenGroups.length > 0) {
    const { tokens, groups } = processTokenGroups(
      uiQuery.tokenGroups,
      filteringProperties,
    );
    if (tokens.length > 0) {
      result.tokens = tokens;
    }
    if (groups.length > 0) {
      result.groups = groups;
    }
  }

  // Handle tokens
  if (uiQuery.tokens && uiQuery.tokens.length > 0) {
    const transformedTokens = uiQuery.tokens.map(transformToken);
    if (result.tokens) {
      result.tokens = [...result.tokens, ...transformedTokens];
    } else {
      result.tokens = transformedTokens;
    }
  }

  // Add implicit filter to exclude sentinel "no date" value (1970-01-01)
  addDateSentinelExclusion(result, filteringProperties);

  return result;
}

/**
 * Processes tokenGroups array and separates individual tokens from token groups
 * Handles nested token groups recursively
 * This may need to be refactored as we worked on other resources
 */
function processTokenGroups(
  tokenGroups: readonly (PropertyFilterToken | PropertyFilterTokenGroup)[],
  filteringProperties?: ExtendedFilteringProperty[],
): {
  tokens: BackendPropertyFilterToken[];
  groups: BackendPropertyFilterQuery[];
} {
  const tokens: BackendPropertyFilterToken[] = [];
  const groups: BackendPropertyFilterQuery[] = [];

  for (const item of tokenGroups) {
    if (isPropertyFilterTokenGroup(item)) {
      // This is a token group - convert to nested query
      const tokenGroup = item as PropertyFilterTokenGroup;

      // Recursively process the token group (which may contain nested groups)
      const nestedResult = processTokenGroups(
        tokenGroup.tokens,
        filteringProperties,
      );

      const group: BackendPropertyFilterQuery = {
        operation: tokenGroup.operation,
      };

      if (nestedResult.tokens.length > 0) {
        group.tokens = nestedResult.tokens;
      }

      if (nestedResult.groups.length > 0) {
        group.groups = nestedResult.groups;
      }

      // Apply date sentinel exclusion to this nested group if filteringProperties provided
      if (filteringProperties) {
        addDateSentinelExclusion(group, filteringProperties);
      }

      groups.push(group);
    } else {
      // This is a simple token
      const token = item as PropertyFilterToken;
      tokens.push(transformToken(token));
    }
  }

  return { tokens, groups };
}

/**
 * Type guard to check if an item is a PropertyFilterTokenGroup
 */
function isPropertyFilterTokenGroup(
  item: PropertyFilterToken | PropertyFilterTokenGroup,
): item is PropertyFilterTokenGroup {
  return "tokens" in item && "operation" in item;
}

/**
 * Transforms a single token from UI format to backend format
 * Handles multiselect enum fields by converting arrays to IN/NOT_IN operators
 * Also cleans "(Guest)" suffix from owner field values
 */
function transformToken(
  uiToken: PropertyFilterToken,
): BackendPropertyFilterToken {
  const isArray = Array.isArray(uiToken.value);

  // Handle multiselect enum fields (array values)
  if (isArray) {
    const values = (uiToken.value as string[]).map((val) =>
      cleanOwnerValue(String(val), uiToken.propertyKey),
    );

    // Convert = to IN and != to NOT_IN for array values
    let operator: BackendPropertyFilterToken["operator"];
    if (uiToken.operator === "=") {
      operator = "in";
    } else if (uiToken.operator === "!=") {
      operator = "not_in";
    } else {
      operator = uiToken.operator as BackendPropertyFilterToken["operator"];
    }

    return {
      propertyKey: uiToken.propertyKey || undefined,
      operator: operator,
      values: values,
    };
  }

  // Handle single value fields
  const value = cleanOwnerValue(String(uiToken.value), uiToken.propertyKey);

  return {
    propertyKey: uiToken.propertyKey || undefined,
    operator: uiToken.operator as BackendPropertyFilterToken["operator"],
    value: value,
  };
}

/**
 * Cleans owner field values by removing "(Guest)" suffix
 */
function cleanOwnerValue(value: string, propertyKey?: string): string {
  if (propertyKey === "owners") {
    return value.replace(/ \(Guest\)$/, "");
  }
  return value;
}

/**
 * Adds implicit filters to exclude date field sentinel value (1970-01-01)
 * when the query contains date field filters with operators <, <=, or !=.
 *
 * Uses > operator instead of != for better OpenSearch performance with range queries.
 *
 * For OR operations: Uses surgical approach to wrap each date token in an AND group
 * with its sentinel exclusion, keeping non-date tokens at the OR level.
 *
 * Operators that DON'T need exclusion:
 * - = (equals): Only matches exact date, automatically excludes 1970-01-01
 * - > (greater than): Already excludes 1970-01-01 naturally if value > 1970-01-01
 * - >= (greater than or equal): Already excludes 1970-01-01 naturally if value > 1970-01-01
 */
function addDateSentinelExclusion(
  query: BackendPropertyFilterQuery,
  filteringProperties: ExtendedFilteringProperty[],
): void {
  // Get all date fields that need exclusion (this also checks if any exist)
  const dateFieldsNeedingExclusion = getDateFieldsNeedingExclusion(
    query,
    filteringProperties,
  );

  if (dateFieldsNeedingExclusion.length === 0) {
    return;
  }

  // Special handling for OR operations: surgical approach
  if (query.operation === "or" && query.tokens) {
    const operatorsNeedingExclusion = ["<", "<=", "!="];
    const dateTokensToWrap: BackendPropertyFilterToken[] = [];
    const remainingTokens: BackendPropertyFilterToken[] = [];

    // Separate date tokens that need exclusion from other tokens
    query.tokens.forEach((token) => {
      if (
        token.propertyKey &&
        dateFieldsNeedingExclusion.includes(token.propertyKey) &&
        operatorsNeedingExclusion.includes(token.operator)
      ) {
        dateTokensToWrap.push(token);
      } else {
        remainingTokens.push(token);
      }
    });

    // If we have date tokens to wrap, create nested AND groups for each
    if (dateTokensToWrap.length > 0) {
      query.tokens = remainingTokens;

      if (!query.groups) {
        query.groups = [];
      }

      // Create an AND group for each date token with its sentinel exclusion
      dateTokensToWrap.forEach((dateToken) => {
        const andGroup: BackendPropertyFilterQuery = {
          operation: "and",
          tokens: [
            dateToken,
            {
              propertyKey: dateToken.propertyKey,
              operator: ">",
              value: DATE_SENTINEL_VALUE,
            },
          ],
        };
        query.groups!.push(andGroup);
      });
    }

    return;
  }

  // For AND operations: add exclusion tokens at the same level
  if (!query.tokens) {
    query.tokens = [];
  }

  dateFieldsNeedingExclusion.forEach((fieldKey) => {
    query.tokens!.push({
      propertyKey: fieldKey,
      operator: ">",
      value: DATE_SENTINEL_VALUE,
    });
  });
}

/**
 * Checks if a property is a date field based on filteringProperties configuration
 */
function isDateField(
  propertyKey: string | undefined,
  filteringProperties: ExtendedFilteringProperty[],
): boolean {
  if (!propertyKey) return false;
  const property = filteringProperties.find((p) => p.key === propertyKey);
  return property?.fieldType === "date";
}

/**
 * Gets all date field keys at the current level that have filters with operators <, <=, or !=.
 * On an "and" level, fields that already carry a > or >= filter excluding the sentinel are dropped.
 */
function getDateFieldsNeedingExclusion(
  query: BackendPropertyFilterQuery,
  filteringProperties: ExtendedFilteringProperty[],
): string[] {
  const operatorsNeedingExclusion = ["<", "<=", "!="];
  const dateFieldsWithFilters = new Set<string>();

  // Collect date fields from tokens at current level only
  query.tokens?.forEach((token) => {
    if (
      token.propertyKey &&
      isDateField(token.propertyKey, filteringProperties) &&
      operatorsNeedingExclusion.includes(token.operator)
    ) {
      dateFieldsWithFilters.add(token.propertyKey);
    }
  });

  // A lower bound only constrains every matching row when it is ANDed in. Under
  // "or" it is a disjunct, so it excludes nothing from the other disjuncts and
  // the sentinel exclusion is still required.
  if (query.operation !== "and") {
    return Array.from(dateFieldsWithFilters);
  }

  // Filter out fields that already have exclusion at current level
  return Array.from(dateFieldsWithFilters).filter(
    (fieldKey) => !hasExclusionForDateField(query, fieldKey),
  );
}

/**
 * Checks if there's already a > or >= operator for the given date field at the current level
 * that would naturally exclude the sentinel value
 */
function hasExclusionForDateField(
  query: BackendPropertyFilterQuery,
  dateFieldKey: string,
): boolean {
  return (
    query.tokens?.some(
      (token) =>
        token.propertyKey === dateFieldKey &&
        ((token.operator === ">" && token.value! >= DATE_SENTINEL_VALUE) ||
          (token.operator === ">=" && token.value! > DATE_SENTINEL_VALUE)),
    ) || false
  );
}
